import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Clock, MessageCircle, Phone, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ShareButton } from '@/components/ShareButton';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar } from '@/components/Avatar';
import { StarRating } from '@/components/StarRating';
import { ReviewsSection } from '@/components/ReviewsSection';
import { formatAvailabilitySlot } from '@/components/AvailabilitySlotsPicker';
import { useSellerRating } from '@/hooks/useSellerRating';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_IMG = 'https://images.pexels.com/photos/1640775/pexels-photo-1640775.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const imgUrl = (path) => (path ? `${process.env.REACT_APP_BACKEND_URL}${path}` : FALLBACK_IMG);

const SellerRatingLine = ({ sellerId }) => {
  const rating = useSellerRating(sellerId);
  if (!rating.total_reviews) return null;
  return (
    <div className="mb-2">
      <StarRating rating={rating.average_rating} size={14} showValue totalReviews={rating.total_reviews} />
    </div>
  );
};

export const ListingDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const images = listing?.images?.length ? listing.images : [];
  const hasImages = images.length > 0;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const nextImage = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % images.length));
  }, [images.length]);
  const prevImage = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length));
  }, [images.length]);

  useEffect(() => {
    fetchListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') nextImage();
      else if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, nextImage, prevImage]);

  // Inject Product JSON-LD once the listing loads so search engines + AI
  // assistants (Google, Perplexity, ChatGPT, Claude) can index the dish
  // accurately. Cleans up on unmount.
  useEffect(() => {
    if (!listing) return;
    const absImages = (listing.images || []).map((img) =>
      img.startsWith('http') ? img : `${window.location.origin}${img}`
    );
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': `${window.location.origin}/listing/${listing.id}`,
      name: listing.title,
      description: listing.description,
      image: absImages,
      category: listing.cuisine ? `${listing.cuisine} cuisine` : 'Homemade food',
      brand: {
        '@type': 'Organization',
        name: seller?.name || listing.seller_name || 'Home cook'
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'AUD',
        price: String(listing.price ?? 0),
        availability:
          (listing.quantity ?? listing.quantity_available ?? 0) > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        url: `${window.location.origin}/listing/${listing.id}`,
        seller: {
          '@type': 'Person',
          name: seller?.name || listing.seller_name || 'Home cook'
        }
      }
    };
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.text = JSON.stringify(jsonLd);
    s.setAttribute('data-listing-jsonld', listing.id);
    document.head.appendChild(s);
    return () => {
      const tag = document.querySelector(`script[data-listing-jsonld="${listing.id}"]`);
      if (tag) tag.remove();
    };
  }, [listing, seller]);

  const fetchListing = async () => {
    try {
      const { data } = await axios.get(`${API}/listings/${id}`);
      setListing(data);
      
      const { data: sellerData } = await axios.get(`${API}/users/${data.seller_id}`);
      setSeller(sellerData);
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast.error('Listing not found');
      navigate('/listings');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageSeller = () => {
    if (!user) {
      toast.error('Please login to message seller');
      navigate('/login');
      return;
    }
    navigate(`/messages?listing_id=${id}&seller_id=${listing.seller_id}`);
  };

  const handleWhatsApp = () => {
    if (seller?.phone) {
      const message = encodeURIComponent(`Hi, I'm interested in your ${listing.title} listing on eatfromlocal.com`);
      window.open(`https://wa.me/${seller.phone.replace(/\D/g, '')}?text=${message}`, '_blank');
    } else {
      toast.error('Seller phone number not available');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F6F0' }}>
        <p style={{ fontFamily: 'Manrope' }}>Loading...</p>
      </div>
    );
  }

  if (!listing) return null;

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7" data-testid="listing-images">
            <button
              type="button"
              onClick={() => hasImages && setLightboxIndex(0)}
              className="block w-full aspect-[4/3] rounded-md overflow-hidden border border-[#E5E0D8] group relative"
              style={{ cursor: hasImages ? 'zoom-in' : 'default' }}
              disabled={!hasImages}
              data-testid="main-image-btn"
              aria-label="Open image gallery"
            >
              <img
                src={hasImages ? imgUrl(images[0]) : FALLBACK_IMG}
                alt={listing.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
              {images.length > 1 && (
                <span
                  className="absolute bottom-3 right-3 px-3 py-1 rounded-full text-white"
                  style={{ backgroundColor: 'rgba(26, 26, 26, 0.75)', fontFamily: 'Manrope', fontSize: '12px', fontWeight: 500, backdropFilter: 'blur(4px)' }}
                  data-testid="image-count-badge"
                >
                  1 / {images.length}
                </span>
              )}
            </button>

            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-3" data-testid="thumbnail-grid">
                {images.slice(1, 5).map((img, idx) => {
                  const actualIndex = idx + 1;
                  return (
                    <button
                      key={actualIndex}
                      type="button"
                      onClick={() => setLightboxIndex(actualIndex)}
                      className="aspect-square rounded-md overflow-hidden border border-[#E5E0D8] hover:border-[#D95A41] transition-colors"
                      style={{ cursor: 'zoom-in' }}
                      data-testid={`thumbnail-${actualIndex}`}
                      aria-label={`View image ${actualIndex + 1}`}
                    >
                      <img
                        src={imgUrl(img)}
                        alt={`${listing.title} ${actualIndex + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-8 p-6 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }}>
              <h2 
                className="text-3xl mb-4 tracking-tight" 
                style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
                data-testid="listing-title"
              >
                {listing.title}
              </h2>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {listing.cuisine && (
                  <span
                    className="px-3 py-1 rounded text-sm"
                    style={{ backgroundColor: '#F2EFEB', color: '#5C5C5C', fontFamily: 'Manrope', border: '1px solid #E5E0D8' }}
                    data-testid="cuisine-tag"
                  >
                    {listing.cuisine} cuisine
                  </span>
                )}
                {listing.dietary_tags?.map((tag, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1 rounded text-sm"
                    style={{ backgroundColor: '#2E5C41', color: '#FFFFFF', fontFamily: 'Manrope' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="mb-6" style={{ fontFamily: 'Manrope', color: '#1A1A1A', lineHeight: '1.6' }}>
                {listing.description}
              </p>

              {listing.ingredients && (
                <div className="mb-6">
                  <h3 
                    className="text-xl mb-2 tracking-tight" 
                    style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}
                  >
                    Ingredients
                  </h3>
                  <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', lineHeight: '1.6' }}>
                    {listing.ingredients}
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2 text-sm" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
                <div className="flex items-center gap-2">
                  <MapPin size={16} strokeWidth={1.5} />
                  <span>{listing.location}</span>
                </div>
                {listing.availability_dates?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Clock size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                    <div className="flex flex-col gap-1">
                      {listing.availability_dates.map((slot, idx) => (
                        <span key={idx} data-testid={`avail-slot-${idx}`}>
                          {slot.includes('|') ? formatAvailabilitySlot(slot) : slot}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {seller && (
              <div className="mt-6 p-6 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }}>
                <h3 
                  className="text-2xl mb-4 tracking-tight" 
                  style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}
                >
                  About the Cook
                </h3>
                <div className="flex items-start gap-4">
                  <Avatar name={seller.name} imageUrl={seller.profile_picture} size={64} />
                  <div className="flex-1">
                    <Link to={`/profile/${seller.id}`}>
                      <h4 
                        className="text-xl mb-1 tracking-tight hover:underline" 
                        style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#D95A41' }}
                        data-testid="seller-name"
                      >
                        {seller.name}
                      </h4>
                    </Link>
                    <SellerRatingLine sellerId={seller.id} />
                    <p className="mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#5C5C5C' }}>
                      {seller.location} · Responds {seller.response_time}
                    </p>
                    {seller.bio && (
                      <p style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#1A1A1A', lineHeight: '1.6' }}>
                        {seller.bio}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {listing && (
              <ReviewsSection sellerId={listing.seller_id} listingId={listing.id} />
            )}
          </div>

          <div className="md:col-span-5">
            <div 
              className="sticky top-24 p-6 rounded-md border border-[#E5E0D8]" 
              style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(26, 26, 26, 0.08)' }}
              data-testid="order-widget"
            >
              <div className="mb-6">
                <span 
                  className="text-4xl tracking-tight" 
                  style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#D95A41' }}
                  data-testid="listing-price"
                >
                  ${listing.price}
                </span>
                <span style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}> per portion</span>
              </div>

              <div className="mb-6">
                <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  max={listing.quantity}
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                  style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                  data-testid="quantity-selector"
                />
                <p className="mt-1" style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}>
                  {listing.quantity} available
                </p>
              </div>

              <div className="mb-6">
                <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Fulfillment Options
                </label>
                {listing.fulfillment_options?.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#2E5C41' }}></div>
                    <span style={{ fontFamily: 'Manrope', fontSize: '14px' }}>{option}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={handleMessageSeller}
                  className="w-full rounded-md py-3"
                  style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600 }}
                  data-testid="message-cook-btn"
                >
                  <MessageCircle size={18} className="mr-2" />
                  Message Cook
                </Button>
                
                {seller?.phone && (
                  <Button 
                    onClick={handleWhatsApp}
                    variant="outline"
                    className="w-full rounded-md py-3"
                    style={{ fontFamily: 'Manrope', fontWeight: 600, borderColor: '#2E5C41', color: '#2E5C41' }}
                    data-testid="whatsapp-btn"
                  >
                    <Phone size={18} className="mr-2" />
                    Connect via WhatsApp
                  </Button>
                )}

                <div className="pt-1" data-testid="detail-share-wrapper">
                  <ShareButton
                    url={typeof window !== 'undefined' ? window.location.href : ''}
                    title={listing.title}
                    text={listing.description ? listing.description.slice(0, 140) : ''}
                    variant="full"
                    testIdSuffix="detail"
                  />
                </div>
              </div>

              <div className="mt-6 p-4 rounded-md" style={{ backgroundColor: '#FEF3C7' }}>
                <p style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#92400E' }}>
                  <strong>Tip:</strong> Pay upon pickup and verify food handling practices!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {lightboxIndex !== null && hasImages && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(15, 15, 15, 0.94)', backdropFilter: 'blur(6px)' }}
          onClick={closeLightbox}
          data-testid="lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-5 right-5 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="lightbox-close"
            aria-label="Close gallery"
          >
            <X size={28} strokeWidth={1.5} />
          </button>

          <div
            className="absolute top-5 left-5 px-3 py-1 rounded-full text-white/90"
            style={{ backgroundColor: 'rgba(255,255,255,0.08)', fontFamily: 'Manrope', fontSize: '13px' }}
            data-testid="lightbox-counter"
          >
            {lightboxIndex + 1} / {images.length}
          </div>

          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-3 md:left-6 p-3 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="lightbox-prev"
              aria-label="Previous image"
            >
              <ChevronLeft size={36} strokeWidth={1.5} />
            </button>
          )}

          <img
            src={imgUrl(images[lightboxIndex])}
            alt={`${listing.title} ${lightboxIndex + 1}`}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-3 md:right-6 p-3 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="lightbox-next"
              aria-label="Next image"
            >
              <ChevronRight size={36} strokeWidth={1.5} />
            </button>
          )}

          {images.length > 1 && (
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 px-3 py-2 rounded-full"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-dots"
            >
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="rounded-full transition-all"
                  style={{
                    width: i === lightboxIndex ? '24px' : '8px',
                    height: '8px',
                    backgroundColor: i === lightboxIndex ? '#D95A41' : 'rgba(255,255,255,0.4)'
                  }}
                  aria-label={`Go to image ${i + 1}`}
                  data-testid={`lightbox-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};